import { useMsal } from '@azure/msal-react'
import {
  faChevronDown,
  faEyeSlash,
  faPaintBrush,
  faPencil,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Accordion,
  ActionIcon,
  Button,
  Divider,
  Group,
  Image,
  Modal,
  Select,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActiveIcon from '../../assets/statusIcons/Active.svg'
import ArchivedIcon from '../../assets/statusIcons/Archived.svg'
import DeniedIcon from '../../assets/statusIcons/Denied.svg'
import ExpiredIcon from '../../assets/statusIcons/Expired.svg'
import PendingIcon from '../../assets/statusIcons/Pending.svg'
import UpcomingIcon from '../../assets/statusIcons/Upcoming.svg'
import { AdCard } from '../../components/AdCard'
import { AdCardSkeleton } from '../../components/AdCardSkeleton'
import { DenyAdModal } from '../../components/DenyAdModal'
import { AllLocationsBundleRow } from '../../components/dashboard/AllLocationsBundleRow'
import { usePendingApproval } from '../../contexts/PendingApprovalContext'
import { useSessionInfo } from '../../contexts/SessionContext'
import {
  AllLocationsAdvertisementRef,
  fetchAllLocationsAdvertisements,
  fetchHostAds,
  fetchMyAdvertisements,
  updateDeviceAdvertisementApprovalStatus,
} from '../../data/advertisement.service'
import { fetchLocationsByOrganizationId } from '../../data/location.service'
import { DeviceAdvertisementsForHost } from '../../models/DeviceAdvertisementsForHost'
import { Location } from '../../models/Location'
import { AdStatuses, COLORS } from '../../utils/constants'
import { AdStatusTooltip } from '../../components/dashboard/AdStatusTooltip'
import { HostSharePanel } from '../../components/dashboard/HostSharePanel'

const getStatusIcon = (status: string) => {
  switch (status) {
    case AdStatuses.PendingContentCheck:
    case AdStatuses.PendingApproval:
      return PendingIcon
    case AdStatuses.Running:
      return ActiveIcon
    case AdStatuses.Upcoming:
      return UpcomingIcon
    case AdStatuses.Completed:
      return ArchivedIcon
    case 'Expired':
      return ExpiredIcon
    case AdStatuses.Denied:
      return DeniedIcon
    case 'Archived':
      return ArchivedIcon
    case AdStatuses.Disabled || AdStatuses.PendingDesign:
      return null
    case AdStatuses.PendingAdvertiserApproval:
      return PendingIcon
  }
}

const getStatusTitle = (status: string) => {
  switch (status) {
    case AdStatuses.PendingContentCheck:
      return 'Pending Content Check'
    case AdStatuses.PendingApproval:
      return 'Pending Host Approval'
    case AdStatuses.Running:
      return 'Active Advertisements'
    case AdStatuses.Upcoming:
      return 'Upcoming Advertisements'
    case AdStatuses.Completed:
      return 'Completed Advertisements'
    case 'Expired':
      return 'Expired Advertisements'
    case AdStatuses.Denied:
      return 'Denied Advertisements'
    case 'Archived':
      return 'Archived Advertisements'
    case AdStatuses.Disabled:
      return 'Disabled Advertisements'
    case AdStatuses.PendingDesign:
      return 'Pending Design'
    case AdStatuses.PendingAdvertiserApproval:
      return 'Pending Your Approval'
    case AdStatuses.ChangesRequested:
      return 'Changes Requested'
    default:
      return 'Advertisements'
  }
}

interface AdvertisementsPageProps {
  isHostingView?: boolean
}

export default function AdvertisementsPage({
  isHostingView = false,
}: AdvertisementsPageProps) {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const { refreshCount } = usePendingApproval()
  const navigate = useNavigate()
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [locations, setLocations] = useState<Location[]>([])
  const [ads, setAds] = useState<DeviceAdvertisementsForHost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedAd, setSelectedAd] =
    useState<DeviceAdvertisementsForHost | null>(null)
  const [adToDeny, setAdToDeny] = useState<DeviceAdvertisementsForHost | null>(
    null,
  )
  const [isDenying, setIsDenying] = useState(false)
  const [approvingAdId, setApprovingAdId] = useState<number | null>(null)
  const [allLocationsBundles, setAllLocationsBundles] = useState<AllLocationsAdvertisementRef[]>([])

  // Exclude all-locations bundle device rows from the per-device status groups;
  // they are aggregated into AllLocationsBundleRow components instead.
  const bundleIdSet = new Set(allLocationsBundles.map((b) => b.advertisementId))
  const nonBundleAds = isHostingView
    ? ads
    : ads.filter((ad) => !bundleIdSet.has(ad.advertisementId))

  const statusGroups = {
    PendingContentCheck: nonBundleAds.filter(
      (ad) => ad.status === AdStatuses.PendingContentCheck,
    ),
    PendingApproval: nonBundleAds.filter(
      (ad) => ad.status === AdStatuses.PendingApproval,
    ),
    PendingDesign: nonBundleAds.filter((ad) => ad.status === AdStatuses.PendingDesign),
    PendingAdvertiserApproval: nonBundleAds.filter(
      (ad) => ad.status === AdStatuses.PendingAdvertiserApproval,
    ),
    ChangesRequested: nonBundleAds.filter(
      (ad) => ad.status === AdStatuses.ChangesRequested,
    ),
    Running: nonBundleAds.filter((ad) => ad.status === AdStatuses.Running),
    Upcoming: nonBundleAds.filter((ad) => ad.status === AdStatuses.Upcoming),
    Completed: nonBundleAds.filter((ad) => ad.status === AdStatuses.Completed),
    Expired: nonBundleAds.filter((ad) => ad.status === 'Expired'),
    Denied: nonBundleAds.filter((ad) => ad.status === AdStatuses.Denied),
    Archived: nonBundleAds.filter((ad) => ad.status === 'Archived'),
    Disabled: nonBundleAds.filter((ad) => ad.status === AdStatuses.Disabled),
  }

  useEffect(() => {
    if (!session?.organizationId || !isHostingView) return

    const fetchLocationsList = async () => {
      try {
        const fetchedLocations = await fetchLocationsByOrganizationId(
          session.organizationId,
        )
        setLocations(fetchedLocations)
      } catch (error) {
        notifications.show({
          title: 'Error loading locations',
          message: 'Unable to load locations. Please try again.',
          color: 'red',
        })
      }
    }
    fetchLocationsList()
  }, [instance, session, isHostingView])

  useEffect(() => {
    if (!session?.organizationId) return

    const fetchAds = async () => {
      setIsLoading(true)
      try {
        let fetchedAds: DeviceAdvertisementsForHost[]
        if (isHostingView) {
          const locationId = selectedLocation
            ? parseInt(selectedLocation)
            : null
          fetchedAds = await fetchHostAds(
            session.organizationId,
            locationId,
            instance,
          )
        } else {
          const [myAds, bundleRefs] = await Promise.all([
            fetchMyAdvertisements(session.organizationId, instance),
            fetchAllLocationsAdvertisements(session.organizationId, instance),
          ])
          fetchedAds = myAds
          setAllLocationsBundles(bundleRefs)
        }
        setAds(fetchedAds)
      } catch (error) {
        notifications.show({
          title: 'Error loading advertisements',
          message: 'Unable to load advertisements. Please try again.',
          color: 'red',
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchAds()
  }, [selectedLocation, instance, session, isHostingView])

  const handleLocationChange = async (value: string | null) => {
    if (!session?.organizationId) return

    setSelectedLocation(value || '')
    setIsLoading(true)
    try {
      const locationId = value ? parseInt(value) : null
      const fetchedAds = await fetchHostAds(
        session.organizationId,
        locationId,
        instance,
      )
      setAds(fetchedAds)
    } catch (error) {
      notifications.show({
        title: 'Error loading ads',
        message: 'Unable to load advertisements. Please try again.',
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleApprove = async (adId: number) => {
    try {
      setApprovingAdId(adId)
      const updatedAd = await updateDeviceAdvertisementApprovalStatus(
        {
          id: adId,
          isApproved: true,
        },
        instance,
      )

      setAds((prevAds) =>
        prevAds.map((ad) => (ad.id === adId ? updatedAd : ad)),
      )

      // Refresh the pending approval count
      await refreshCount()

      notifications.show({
        title: 'Success',
        message: 'Advertisement has been approved',
        color: 'green',
      })
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to approve advertisement',
        color: 'red',
      })
    } finally {
      setApprovingAdId(null)
    }
  }

  const handleDeny = async (ad: DeviceAdvertisementsForHost) => {
    setAdToDeny(ad)
  }

  const handleConfirmDeny = async (adId: number, denialReason: string) => {
    if (!adToDeny || !denialReason.trim()) {
      notifications.show({
        title: 'Error',
        message: 'Please provide a reason for denial',
        color: 'red',
      })
      return
    }

    try {
      setIsDenying(true)
      const updatedAd = await updateDeviceAdvertisementApprovalStatus(
        {
          id: adId,
          isApproved: false,
          denialReason: denialReason.trim(),
        },
        instance,
      )

      setAds((prevAds) =>
        prevAds.map((ad) => (ad.id === adToDeny.id ? updatedAd : ad)),
      )

      // Refresh the pending approval count
      await refreshCount()

      notifications.show({
        title: 'Success',
        message: 'Advertisement has been denied',
        color: 'green',
      })

      setAdToDeny(null)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to deny advertisement',
        color: 'red',
      })
    } finally {
      setIsDenying(false)
    }
  }

  const handleAdUpdated = (updatedAd: DeviceAdvertisementsForHost) => {
    setAds((prevAds) =>
      prevAds.map((ad) => (ad.id === updatedAd.id ? updatedAd : ad)),
    )
  }

  const handleStartAdvertising = () => {
    navigate('/')
  }

  return (
    <>
      <Stack gap='xl'>
        <Group
          justify='space-between'
          align='center'
          h={{ base: 'auto', sm: 70 }}
          px={24}
          py={{ base: 16, sm: 0 }}
          style={{
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Title fw={500} fz={{ base: 'h3', sm: 'h2' }}>
            {isHostingView ? 'Ads at My Locations' : 'My Advertisements'}
          </Title>
          {isHostingView && locations.length > 1 && (
            <Group
              gap='xs'
              wrap='wrap'
              styles={{
                root: {
                  '@media (maxWidth: 48em)': {
                    alignSelf: 'flex-start',
                  },
                },
              }}
            >
              <Text c={COLORS.LightGreen} fw={500}>
                Location:
              </Text>
              <Select
                placeholder='All Locations'
                value={selectedLocation}
                onChange={handleLocationChange}
                data={[
                  { value: '', label: 'All Locations' },
                  ...locations.map((loc) => ({
                    value: loc.id?.toString() || '',
                    label: loc.name || '',
                  })),
                ]}
                rightSection={
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    size='sm'
                    color='white'
                  />
                }
                variant='unstyled'
                styles={{
                  input: {
                    borderRadius: '30px',
                    backgroundColor: COLORS.LightGreen,
                    color: 'white',
                    padding: '8px 16px',
                  },
                  dropdown: {
                    borderRadius: '8px',
                  },
                  option: {
                    borderRadius: '30px',
                    '&[dataSelected]': {
                      backgroundColor: COLORS.DarkGreen,
                      color: 'white',
                    },
                  },
                }}
              />
            </Group>
          )}
        </Group>

        <Stack gap='xl' px={24} pb={24}>
          {isLoading ? (
            <Stack gap='md'>
              <Skeleton height={32} width={300} />
              {[...Array(3)].map((_, index) => (
                <AdCardSkeleton key={index} />
              ))}
            </Stack>
          ) : (
            <>
              {!isHostingView && allLocationsBundles.length > 0 && (() => {
                const activeBundles = allLocationsBundles.filter((b) => !b.isPast)
                const pastBundles = allLocationsBundles.filter((b) => b.isPast)
                return (
                  <Stack gap='md'>
                    <Title order={4} c={COLORS.LightGreen}>
                      All Locations Bundles
                    </Title>

                    {activeBundles.map((bundle) => (
                      <AllLocationsBundleRow
                        key={bundle.advertisementId}
                        advertisementId={bundle.advertisementId}
                        instance={instance}
                      />
                    ))}

                    {pastBundles.length > 0 && (
                      <Accordion variant='separated' radius='md'>
                        <Accordion.Item value='past'>
                          <Accordion.Control>
                            <Text fw={500} c='dimmed'>
                              Past Bundles ({pastBundles.length})
                            </Text>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack gap='md'>
                              {pastBundles.map((bundle) => (
                                <AllLocationsBundleRow
                                  key={bundle.advertisementId}
                                  advertisementId={bundle.advertisementId}
                                  instance={instance}
                                />
                              ))}
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                    )}
                  </Stack>
                )
              })()}
              {Object.entries(statusGroups).map(([status, ads]) => {
                if (ads.length === 0) return null
                return (
                  <Stack key={status} gap='md'>
                    <Group gap='xs'>
                      {status === AdStatuses.Disabled ? (
                        <FontAwesomeIcon
                          icon={faEyeSlash}
                          style={{
                            color: COLORS.LightGreen,
                            width: '24px',
                            height: '24px',
                          }}
                        />
                      ) : status === AdStatuses.PendingDesign ? (
                        <FontAwesomeIcon
                          icon={faPaintBrush}
                          style={{
                            color: COLORS.LightGreen,
                            width: '24px',
                            height: '24px',
                          }}
                        />
                      ) : status === AdStatuses.ChangesRequested ? (
                        <FontAwesomeIcon
                          icon={faPencil}
                          style={{
                            color: COLORS.LightGreen,
                            width: '24px',
                            height: '24px',
                          }}
                        />
                      ) : (
                        <Image
                          src={getStatusIcon(status)}
                          width={24}
                          height={24}
                          alt={status}
                        />
                      )}
                      <AdStatusTooltip
                        status={status}
                        isHostMode={isHostingView}
                      >
                        <Title order={4} c={COLORS.LightGreen}>
                          {getStatusTitle(status)}
                        </Title>
                      </AdStatusTooltip>
                    </Group>
                    {ads.map((ad) => (
                      <AdCard
                        key={ad.id}
                        ad={ad}
                        isPending={status === AdStatuses.PendingApproval}
                        onApprove={isHostingView ? handleApprove : undefined}
                        onDeny={isHostingView ? handleDeny : undefined}
                        onExpand={setSelectedAd}
                        approvingAdId={approvingAdId}
                        onAdUpdated={handleAdUpdated}
                        isHostingView={isHostingView}
                      />
                    ))}
                  </Stack>
                )
              })}
              {Object.values(statusGroups).every((grpAds) => grpAds.length === 0) &&
                (isHostingView || allLocationsBundles.length === 0) && (
                  isHostingView ? (
                    <HostSharePanel
                      locationId={selectedLocation ? parseInt(selectedLocation) : locations[0]?.id}
                      locationName={
                        selectedLocation
                          ? locations.find((l) => l.id?.toString() === selectedLocation)?.name
                          : locations[0]?.name
                      }
                    />
                  ) : (
                    <Stack align='center' gap='md' py='xl'>
                      <Text size='lg' c='dimmed' ta='center'>
                        No advertisements to show
                      </Text>
                      <Button onClick={handleStartAdvertising} size='md'>
                        Start Advertising
                      </Button>
                    </Stack>
                  )
                )}
              <Divider />
            </>
          )}
        </Stack>
      </Stack>

      <Modal
        opened={!!selectedAd}
        onClose={() => setSelectedAd(null)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          content: {
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          inner: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'black',
          }}
        >
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setSelectedAd(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
          {selectedAd && (
            <div
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 'calc(90vh * 16/9)',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={selectedAd.adImageUrl}
                alt={selectedAd.organizationName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </Modal>

      {isHostingView && (
        <DenyAdModal
          isOpen={!!adToDeny}
          onClose={() => setAdToDeny(null)}
          onConfirm={handleConfirmDeny}
          isDenying={isDenying}
          adToDeny={adToDeny}
        />
      )}
    </>
  )
}
